import { Check, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'

export interface CaseDescriptionViewProps {
  description: string | null
  /** True while the connected save is in flight. */
  saving: boolean
  /** Persist the edited description. Resolve to close the editor; reject to keep it open. */
  onSave: (draft: string) => Promise<void>
  /** Start in edit mode — used by stories to show the editor. */
  defaultEditing?: boolean
}

/**
 * Pure presentation + local edit state for the overview's case description.
 * The connected wrapper in `features/case-overview` owns the persistence
 * (mutation + toast) behind `onSave`.
 */
export function CaseDescriptionView({
  description,
  saving,
  onSave,
  defaultEditing = false,
}: CaseDescriptionViewProps) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(defaultEditing)
  const [draft, setDraft] = useState(description || '')

  const handleEdit = () => {
    setDraft(description || '')
    setEditing(true)
  }

  const handleCancel = () => {
    setEditing(false)
    setDraft(description || '')
  }

  const handleSave = async () => {
    try {
      await onSave(draft)
      setEditing(false)
    } catch {
      // Keep the editor open so the analyst can retry; the wrapper surfaces the error.
    }
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t('overview.description')}
          </h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="xs"
              onClick={handleCancel}
              disabled={saving}
              className="max-md:h-9"
            >
              <X />
              {t('common.cancel')}
            </Button>
            <Button
              size="xs"
              onClick={handleSave}
              disabled={saving}
              loading={saving}
              className="max-md:h-9"
            >
              {!saving && <Check />}
              {t('common.save')}
            </Button>
          </div>
        </div>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder={t('overview.descriptionPlaceholder')}
          rows={4}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-base placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring resize-y md:text-sm"
        />
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {t('overview.description')}
        </h3>
        <Button
          variant="ghost"
          size="xs"
          onClick={handleEdit}
          className="text-muted-foreground max-md:h-9"
        >
          <Pencil />
          {t('common.edit')}
        </Button>
      </div>
      {description ? (
        <p className="text-sm text-foreground/80 whitespace-pre-wrap">{description}</p>
      ) : (
        <p className="text-sm text-muted-foreground/50 italic">{t('overview.noDescription')}</p>
      )}
    </div>
  )
}
